import React, {useEffect, useMemo, useState} from 'react';
import styles from './CustomPageTemplate.module.scss';
import {useSearchParams} from 'react-router-dom';
import {getCustomPageDataByLink} from '../../services/createCustomHtmlService';
import {generateHTML} from '@tiptap/html';
import {mergeAttributes} from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';

const getImageStyle = ({width, align}) => {
    const safeWidth = width || '100%';
    const safeAlign = align || 'center';
    const baseStyle = `width:${safeWidth};max-width:100%;height:auto;display:block;`;

    if (safeAlign === 'left') return `${baseStyle}margin-left:0;margin-right:auto;`;
    if (safeAlign === 'right') return `${baseStyle}margin-left:auto;margin-right:0;`;
    return `${baseStyle}margin-left:auto;margin-right:auto;`;
};

const ResizableImage = Image.extend({
    addAttributes() {
        const parentAttributes = this.parent?.() || {};

        return {
            ...parentAttributes,
            width: {
                default: '100%',
                parseHTML: (element) => element.style.width || element.getAttribute('width') || '100%',
                renderHTML: (attributes) => {
                    return {
                        style: getImageStyle(attributes)
                    };
                }
            },
            align: {
                default: 'center',
                parseHTML: (element) => element.getAttribute('data-align') || 'center',
                renderHTML: (attributes) => ({
                    'data-align': attributes.align || 'center'
                })
            },
            href: {
                default: null,
                parseHTML: (element) => (
                    element.parentElement?.tagName === 'A'
                        ? element.parentElement.getAttribute('href')
                        : null
                ),
                renderHTML: () => ({})
            },
            target: {
                default: '_blank',
                parseHTML: (element) => (
                    element.parentElement?.tagName === 'A'
                        ? (element.parentElement.getAttribute('target') || '_blank')
                        : '_blank'
                ),
                renderHTML: () => ({})
            },
            rel: {
                default: 'noopener noreferrer',
                parseHTML: (element) => (
                    element.parentElement?.tagName === 'A'
                        ? (element.parentElement.getAttribute('rel') || 'noopener noreferrer')
                        : 'noopener noreferrer'
                ),
                renderHTML: () => ({})
            }
        };
    },
    renderHTML({HTMLAttributes, node}) {
        const nodeAttributes = node?.attrs || {};
        const href = nodeAttributes.href || HTMLAttributes.href;
        const target = nodeAttributes.target || HTMLAttributes.target;
        const rel = nodeAttributes.rel || HTMLAttributes.rel;
        const {href: _href, target: _target, rel: _rel, ...imgAttributes} = HTMLAttributes;
        const imageTag = ['img', mergeAttributes(this.options.HTMLAttributes, imgAttributes)];

        if (href) {
            return ['a', {href, target: target || '_blank', rel: rel || 'noopener noreferrer'}, imageTag];
        }

        return imageTag;
    }
});

const parseEditorContent = (rawContent) => {
    if (!rawContent) return null;

    if (typeof rawContent === 'string') {
        try {
            const parsed = JSON.parse(rawContent);
            return parsed?.type ? parsed : null;
        } catch {
            return null;
        }
    }

    return rawContent?.type ? rawContent : null;
};

const normalizePage = (responseData) => {
    if (!responseData) return null;
    if (Array.isArray(responseData)) return responseData[0] || null;
    if (Array.isArray(responseData?.rows)) return responseData.rows[0] || null;
    if (responseData?.data && Array.isArray(responseData.data)) return responseData.data[0] || null;
    return responseData;
};

const CustomPageTemplate = () => {
    const [searchParams] = useSearchParams();
    const location = searchParams.get('l');
    const [pageData, setPageData] = useState(null);

    const getCustomPageDataByLocation = async () => {
        const response = await getCustomPageDataByLink(location);
        const normalizedPage = normalizePage(response?.data);
        setPageData(normalizedPage);
    };

    useEffect(() => {
        getCustomPageDataByLocation();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const editorJson = parseEditorContent(pageData?.content_json);

    const renderedEditorHtml = useMemo(() => {
        if (!editorJson) return '';

        try {
            return generateHTML(editorJson, [
                StarterKit,
                Underline,
                Link,
                ResizableImage,
                TextAlign.configure({types: ['heading', 'paragraph']})
            ]);
        } catch {
            return '';
        }
    }, [editorJson]);

    return (
        <div className={styles.container}>
            <div className={styles.pageItyself}>
                <div className={styles.pageContainer}>
                    {renderedEditorHtml ? (
                        <div className={styles.richContent} dangerouslySetInnerHTML={{__html: renderedEditorHtml}}/>
                    ) : (
                        <div className={styles.emptyState}>This page has no published content yet.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomPageTemplate;
